import axios from 'axios';

/**
 * Service for managing push notifications and service worker
 */
class PushNotificationService {
    constructor() {
        this.serviceWorkerRegistration = null;
        this.pushSubscription = null;
        this.isSupported = this._checkSupport();
    }

    /**
     * Check if push notifications are supported
     */
    _checkSupport() {
        return 'serviceWorker' in navigator && 
               'PushManager' in window && 
               'Notification' in window;
    }

    /**
     * Initialize the service worker and push notifications
     */
    async initialize() {
        console.debug('PushNotificationService.initialize() called');
        console.debug('Browser support check:', {
            serviceWorker: 'serviceWorker' in navigator,
            PushManager: 'PushManager' in window,
            Notification: 'Notification' in window
        });
        
        if (!this.isSupported) {
            console.warn('Push notifications are not supported in this browser');
            return false;
        }

        try {
            console.debug('Registering service worker at /service-worker.js');
            // Register service worker
            this.serviceWorkerRegistration = await navigator.serviceWorker.register('/service-worker.js');
            console.log('Service Worker registered:', this.serviceWorkerRegistration);

            // Wait for service worker to be ready (activated)
            console.debug('Waiting for service worker to activate...');
            const registration = await navigator.serviceWorker.ready;
            
            // On mobile, the service worker might still be "activating"
            // Wait for it to become "activated"
            if (registration.active && registration.active.state === 'activating') {
                console.debug('Service worker is still activating, waiting for it to become active...');
                await new Promise((resolve) => {
                    const checkActivation = setInterval(() => {
                        if (registration.active && registration.active.state === 'activated') {
                            clearInterval(checkActivation);
                            console.debug('Service worker is now activated');
                            resolve();
                        }
                    }, 100); // Check every 100ms
                    
                    // Timeout after 5 seconds
                    setTimeout(() => {
                        clearInterval(checkActivation);
                        console.debug('Service worker activation timeout, proceeding anyway');
                        resolve();
                    }, 5000);
                });
            }
            
            console.debug('Service worker is ready:', {
                scope: registration.scope,
                active: registration.active ? registration.active.state : 'none',
                waiting: registration.waiting ? registration.waiting.state : 'none',
                installing: registration.installing ? registration.installing.state : 'none',
                pushManager: !!registration.pushManager
            });
            
            // Update our registration reference to the ready one
            this.serviceWorkerRegistration = registration;
            
            console.debug('Service worker registration ready for push subscriptions');

            // Check if websocket connection is ready (but don't fail if it times out)
            try {
                await this._waitForWebsocketConnection();
            } catch (wsError) {
                console.warn('WebSocket connection check failed, continuing anyway:', wsError);
            }

            // Check if we have an existing subscription
            this.pushSubscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
            
            if (this.pushSubscription) {
                console.log('Existing push subscription found:', this.pushSubscription);
                // Send subscription to server to ensure it's up to date
                await this._sendSubscriptionToServer(this.pushSubscription);
            }

            // Listen for service worker updates
            this.serviceWorkerRegistration.addEventListener('updatefound', () => {
                console.log('Service Worker update found');
                const newWorker = this.serviceWorkerRegistration.installing;
                
                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        // New service worker available, prompt for update
                        this._promptForUpdate();
                    }
                });
            });

            // Listen for messages from service worker
            navigator.serviceWorker.addEventListener('message', event => {
                console.log('Message from service worker:', event.data);
                
                if (event.data.type === 'navigate-to-conversation') {
                    // Handle navigation to specific conversation
                    this._navigateToConversation(event.data.conversationId);
                }
            });

            return true;
        } catch (error) {
            console.error('Error initializing push notifications:', error);
            console.error('Error details:', {
                name: error.name,
                message: error.message,
                stack: error.stack
            });
            return false;
        }
    }

    /**
     * Subscribe to push notifications
     */
    async subscribe() {
        console.debug('PushNotificationService.subscribe() called');
        
        if (!this.isSupported || !this.serviceWorkerRegistration) {
            console.error('Service worker not initialized', {
                isSupported: this.isSupported,
                serviceWorkerRegistration: this.serviceWorkerRegistration
            });
            return false;
        }

        try {
            // Get VAPID public key from server
            console.debug('Fetching VAPID public key...');
            const response = await axios.get('/api/push/vapid-public-key');
            console.debug('VAPID response:', response.data);
            
            const vapidPublicKey = response.data.public_key;
            
            if (!vapidPublicKey) {
                console.error('VAPID public key not available');
                return false;
            }

            // Convert VAPID key to Uint8Array
            const convertedVapidKey = this._urlBase64ToUint8Array(vapidPublicKey);
            console.debug('Converted VAPID key:', convertedVapidKey);

            // Subscribe to push notifications
            console.debug('Subscribing to push manager...');
            console.debug('Push manager available:', !!this.serviceWorkerRegistration.pushManager);
            console.debug('Notification permission:', Notification.permission);
            
            try {
                this.pushSubscription = await this.serviceWorkerRegistration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: convertedVapidKey
                });
                console.log('Push subscription successful:', this.pushSubscription);
            } catch (subscribeError) {
                console.error('Push subscription failed:', subscribeError);
                console.error('Error details:', {
                    name: subscribeError.name,
                    message: subscribeError.message,
                    code: subscribeError.code
                });
                
                // Check common error causes
                if (subscribeError.name === 'NotAllowedError') {
                    console.error('Subscription blocked - user denied permission or browser policy');
                } else if (subscribeError.name === 'InvalidStateError') {
                    console.error('Service worker is not in correct state');
                } else if (subscribeError.name === 'NotSupportedError') {
                    console.error('Push notifications not supported on this device/browser');
                }
                
                throw subscribeError;
            }

            // Send subscription to server
            const sent = await this._sendSubscriptionToServer(this.pushSubscription);
            console.debug('Subscription sent to server:', sent);

            return sent;
        } catch (error) {
            console.error('Error subscribing to push notifications:', error);
            if (error.response) {
                console.error('Server response error:', error.response.data);
            }
            return false;
        }
    }

    /**
     * Force subscribe to push notifications (unsubscribe first if needed)
     */
    async forceSubscribe() {
        console.debug('PushNotificationService.forceSubscribe() called');
        
        if (!this.isSupported || !this.serviceWorkerRegistration) {
            console.error('Service worker not initialized for force subscribe', {
                isSupported: this.isSupported,
                serviceWorkerRegistration: !!this.serviceWorkerRegistration,
                registrationState: this.serviceWorkerRegistration ? {
                    active: this.serviceWorkerRegistration.active?.state,
                    waiting: this.serviceWorkerRegistration.waiting?.state,
                    installing: this.serviceWorkerRegistration.installing?.state
                } : null
            });
            return false;
        }

        console.debug('Force subscribe starting with service worker state:', {
            active: this.serviceWorkerRegistration.active?.state,
            waiting: this.serviceWorkerRegistration.waiting?.state,
            installing: this.serviceWorkerRegistration.installing?.state,
            scope: this.serviceWorkerRegistration.scope
        });

        try {
            // First, check if we have an existing subscription and unsubscribe
            console.debug('Checking for existing subscription...');
            const existingSubscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
            console.debug('Existing subscription found:', !!existingSubscription);
            if (existingSubscription) {
                console.debug('Found existing subscription, unsubscribing first...');
                try {
                    await existingSubscription.unsubscribe();
                    console.debug('Successfully unsubscribed from existing subscription');
                } catch (unsubError) {
                    console.warn('Failed to unsubscribe from existing subscription, continuing anyway:', unsubError);
                }
            }

            // Clear our stored subscription
            this.pushSubscription = null;

            // Now perform a fresh subscription
            console.debug('Performing fresh subscription after cleanup...');
            return await this.subscribe();
        } catch (error) {
            console.error('Error in forceSubscribe:', error);
            return false;
        }
    }

    /**
     * Unsubscribe from push notifications
     */
    async unsubscribe() {
        if (!this.pushSubscription) {
            console.log('No push subscription to unsubscribe');
            return true;
        }

        try {
            // Unsubscribe from push
            await this.pushSubscription.unsubscribe();
            
            // Remove subscription from server
            await this._removeSubscriptionFromServer();

            this.pushSubscription = null;
            console.log('Push subscription removed');

            return true;
        } catch (error) {
            console.error('Error unsubscribing from push notifications:', error);
            return false;
        }
    }

    /**
     * BRO-1543: resolve an ALREADY-REGISTERED service worker, read-only.
     *
     * WHY THIS EXISTS. `initialize()` REGISTERS the worker and the page-load caller that
     * used to run it is dead: notification-init.js gates the whole setup on a
     * `<meta name="company-id">` tag that no template has ever output (0 occurrences on
     * develop and master, 0 commits that ever added it). So by the time the preferences
     * panel mounts, `serviceWorkerRegistration` is always null -- and `isSubscribed()`
     * therefore always answered false, for every user, whatever their real state.
     *
     * That is what made the panel offer "Activate" to people who were already subscribed,
     * and pressing it calls forceSubscribe(), which REPLACES a working subscription.
     *
     * This does NOT register anything and does NOT prompt for permission. It asks the
     * browser whether a registration already exists, which is the question the panel
     * needed and the only one it is entitled to ask without a user gesture. Switching the
     * dead page-load auto-subscribe back on is a behaviour change for the whole user base
     * and is with the Product Owner; this is deliberately not that.
     */
    async existingRegistration() {
        if (!this.isSupported) {
            return null;
        }
        if (this.serviceWorkerRegistration) {
            return this.serviceWorkerRegistration;
        }

        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
                this.serviceWorkerRegistration = registration;
            }

            return registration ?? null;
        } catch (error) {
            console.debug('No existing service worker registration:', error);

            return null;
        }
    }

    /**
     * BRO-1543: this browser's current push subscription, or null. Read-only.
     */
    async existingSubscription() {
        const registration = await this.existingRegistration();
        if (!registration) {
            return null;
        }

        try {
            return await registration.pushManager.getSubscription();
        } catch (error) {
            console.debug('Could not read the browser push subscription:', error);

            return null;
        }
    }

    /**
     * Check if user is subscribed to push notifications
     */
    async isSubscribed() {
        if (!this.isSupported || !this.serviceWorkerRegistration) {
            return false;
        }

        try {
            const subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
            return subscription !== null;
        } catch (error) {
            console.error('Error checking push subscription:', error);
            return false;
        }
    }

    /**
     * Send subscription to server
     */
    async _sendSubscriptionToServer(subscription) {
        try {
            const response = await axios.post('/api/push/subscribe', {
                endpoint: subscription.endpoint,
                keys: {
                    p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
                    auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
                }
            });

            console.log('Push subscription sent to server:', response.data);
            return true;
        } catch (error) {
            console.error('Error sending push subscription to server:', error);
            return false;
        }
    }

    /**
     * Remove subscription from server
     */
    async _removeSubscriptionFromServer() {
        try {
            await axios.delete('/api/push/unsubscribe');
            console.log('Push subscription removed from server');
            return true;
        } catch (error) {
            console.error('Error removing push subscription from server:', error);
            return false;
        }
    }

    /**
     * Convert VAPID key from base64 to Uint8Array
     */
    _urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/\-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    /**
     * Navigate to a specific conversation
     */
    _navigateToConversation(conversationId) {
        // Check if we're already in message hub
        if (window.location.pathname === '/message-hub') {
            // Dispatch event to select conversation
            window.dispatchEvent(new CustomEvent('select-conversation', {
                detail: { conversationId }
            }));
        } else {
            // Navigate to message hub with conversation ID
            window.location.href = `/message-hub?conversation=${conversationId}`;
        }
    }

    /**
     * Prompt user to update service worker
     */
    _promptForUpdate() {
        // You could show a toast or modal here
        // For now, we'll just log it
        console.log('New service worker available. Please refresh to update.');
        
        // Dispatch event that UI components can listen to
        window.dispatchEvent(new CustomEvent('service-worker-update-available'));
    }

    /**
     * Force update of service worker
     */
    async update() {
        if (!this.serviceWorkerRegistration) {
            return false;
        }

        try {
            await this.serviceWorkerRegistration.update();
            
            // Tell waiting service worker to skip waiting
            if (this.serviceWorkerRegistration.waiting) {
                this.serviceWorkerRegistration.waiting.postMessage({ type: 'skip-waiting' });
            }
            
            return true;
        } catch (error) {
            console.error('Error updating service worker:', error);
            return false;
        }
    }

    /**
     * Wait for websocket connection to be ready
     */
    async _waitForWebsocketConnection(maxWaitTime = 10000) {
        console.debug('Checking websocket connection readiness...');
        
        const startTime = Date.now();
        
        return new Promise((resolve, reject) => {
            // Check if Echo is available and has a connector
            if (typeof window.Echo === 'undefined') {
                console.warn('Echo is not available, proceeding without websocket check');
                resolve();
                return;
            }

            // Function to check connection status
            const checkConnection = () => {
                try {
                    // Try to access the connector
                    if (window.Echo.connector && window.Echo.connector.socket) {
                        const socket = window.Echo.connector.socket;
                        
                        // Check if socket is connected
                        if (socket.readyState === WebSocket.OPEN) {
                            console.debug('✓ Websocket connection is ready');
                            resolve();
                            return true;
                        }
                    }
                    
                    // Check if we've exceeded max wait time
                    if (Date.now() - startTime > maxWaitTime) {
                        console.warn('Websocket connection check timed out, proceeding anyway');
                        resolve();
                        return true;
                    }
                    
                    return false;
                } catch (error) {
                    console.warn('Error checking websocket connection:', error);
                    resolve(); // Proceed anyway
                    return true;
                }
            };

            // Check immediately
            if (checkConnection()) {
                return;
            }

            // Listen for Echo connection events if available
            if (window.Echo && window.Echo.connector) {
                const originalConnect = window.Echo.connector.connect;
                window.Echo.connector.connect = function(...args) {
                    const result = originalConnect.apply(this, args);
                    console.debug('Echo connected');
                    if (!checkConnection()) {
                        // Still check periodically
                        const interval = setInterval(() => {
                            if (checkConnection()) {
                                clearInterval(interval);
                            }
                        }, 500);
                    }
                    return result;
                };
            }

            // Fallback: check periodically
            const interval = setInterval(() => {
                if (checkConnection()) {
                    clearInterval(interval);
                }
            }, 500);
        });
    }
}

// Export singleton instance
export default new PushNotificationService();